import type { File as ReploFile } from "schemas/generated/file";

import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import Label from "@replo/design-system/components/label/Label";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { X } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { ReploError } from "schemas/errors";
import { z } from "zod";

import Input from "../../../editor/components/common/designSystem/Input";
import { useCurrentProjectContext } from "../../../editor/contexts/CurrentProjectContext";
import useCurrentProjectId from "../../../editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "../../../editor/utils/trpc";
import useDefaultCustomDomain from "../hooks/useDefaultCustomDomain";
import { useEditorStoreActions } from "../store/editorStore";

class OpenGraphImageUploadError extends ReploError {}

const pageSettingsSchema = z.object({
  pageName: z.string().min(1, "Page name is required"),
  pageUrl: z.string().min(1, "URL is required"),
  pageTitle: z.string().min(1, "Page title is required"),
  pageDescription: z.string().min(1, "Page description is required"),
  openGraphImage: z.string().nullable(),
});

type PageSettings = z.infer<typeof pageSettingsSchema>;

export default function PageSettingsPanel({ page }: { page: ReploFile }) {
  const { setPanelView } = useEditorStoreActions();
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProjectContext();
  const defaultCustomDomain = useDefaultCustomDomain();

  const {
    handleSubmit,
    setValue,
    formState: { isDirty, errors },
    watch,
    register,
    control,
    reset,
  } = useForm<PageSettings>({
    resolver: zodResolver(pageSettingsSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      pageName: page.name,
      pageUrl: page.pageRoute ?? "",
      pageTitle: page.pageMetadataTitle ?? "",
      pageDescription: page.pageMetadataDescription ?? "",
      openGraphImage: page.pageMetadataOpenGraphImageUrl,
    },
  });

  // Keep preview URL separate for display purposes
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [isImageUploadLoading, setIsImageUploadLoading] = React.useState(false);
  const [selectedImageFile, setSelectedImageFile] = React.useState<File | null>(
    null,
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { mutate: updatePage, isPending: isSaving } =
    trpc.nextJSProject.updatePage.useMutation({
      onSuccess: () => {
        void trpcUtils.nextJSProject.getPage.invalidate({
          projectId: projectId!,
          id: page.id,
        });

        reset({}, { keepValues: true });
      },
    });

  const { mutateAsync: generateSignedUrlForOpenGraphImage } =
    trpc.shopMetadata.generateSignedUrlForOpenGraphImage.useMutation();

  const handleClose = () => {
    setPanelView("chat");
  };

  const handleDiscard = () => {
    reset({
      pageName: page.name,
      pageUrl: page.pageRoute ?? "",
      pageTitle: page.pageMetadataTitle ?? "",
      pageDescription: page.pageMetadataDescription ?? "",
      openGraphImage: page.pageMetadataOpenGraphImageUrl,
    });
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(page.pageMetadataOpenGraphImageUrl);
    setSelectedImageFile(null);
  };

  const handleSave = async (pageSettings: PageSettings) => {
    if (!projectId) {
      console.error("No project ID available for saving");
      return;
    }

    setIsImageUploadLoading(true);

    let openGraphImageUrl = pageSettings.openGraphImage;

    if (selectedImageFile) {
      const { signedUrl, publicUrl } = await generateSignedUrlForOpenGraphImage(
        {
          projectId,
          fileId: page.id,
          contentType: selectedImageFile.type,
          contentLength: selectedImageFile.size,
        },
      );

      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        body: selectedImageFile,
        headers: {
          "Content-Type": selectedImageFile.type,
          "Content-Length": selectedImageFile.size.toString(),
        },
      });

      if (!uploadResponse.ok) {
        throw new OpenGraphImageUploadError({
          message: `Failed to upload image ${selectedImageFile.name} to Cloudflare`,
          additionalData: { status: uploadResponse.status },
        });
      }

      openGraphImageUrl = publicUrl;

      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(publicUrl);
      setSelectedImageFile(null);
    }

    updatePage({
      projectId,
      id: page.id,
      updates: {
        newName: pageSettings.pageName,
        newPageRoute: pageSettings.pageUrl,
        newPageMetadataTitle: pageSettings.pageTitle,
        newPageMetadataDescription: pageSettings.pageDescription,
        newPageMetadataOpenGraphImageUrl:
          openGraphImageUrl === "PENDING_UPLOAD"
            ? undefined
            : openGraphImageUrl ?? undefined,
      },
    });

    setIsImageUploadLoading(false);
  };

  const handleSetValue = (
    key: keyof PageSettings,
    value: PageSettings[keyof PageSettings],
  ) => {
    setValue(key, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleImageUpload = (file: File) => {
    const isValidFile =
      file.type.startsWith("image/") || /\.(png|jpe?g|webp)$/i.test(file.name);

    if (!isValidFile) {
      return;
    }

    // Just store the file and create preview - don't upload yet
    const objectUrl = URL.createObjectURL(file);
    setImagePreview(objectUrl);
    setSelectedImageFile(file);

    handleSetValue("openGraphImage", "PENDING_UPLOAD");
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleReplaceImage = () => {
    fileInputRef.current?.click();
  };

  React.useEffect(() => {
    if (page.pageMetadataOpenGraphImageUrl && !imagePreview) {
      setImagePreview(page.pageMetadataOpenGraphImageUrl);
    }

    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview, page.pageMetadataOpenGraphImageUrl]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col pb-4 shadow-selectable border-0.5 border-border rounded-lg bg-white relative overflow-hidden">
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="header-h3">Page Settings</h2>
          <IconButton
            variant="tertiary"
            size="base"
            onClick={handleClose}
            icon={<X size={16} />}
          />
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="p-5 space-y-4 border-b border-border">
            <h3 className="header-h4">General</h3>
            <div className="flex flex-col gap-2">
              <Label label="Page Name" size="base" />
              <Input
                {...register("pageName")}
                placeholder="Enter page name"
                size="base"
                validityState={errors.pageName ? "invalid" : "valid"}
              />
              {errors.pageName && (
                <span className="text-danger text-sm font-medium">
                  {errors.pageName.message}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label label="URL" size="base" />
              <Input
                {...register("pageUrl")}
                placeholder="Enter URL"
                size="base"
                validityState={errors.pageUrl ? "invalid" : "valid"}
              />
              {errors.pageUrl && (
                <span className="text-danger text-sm font-medium">
                  {errors.pageUrl.message}
                </span>
              )}
            </div>
          </div>
          <div className="p-6">
            <div>
              <h3 className="header-h4 mb-4">SEO</h3>
              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <Label label="Page Title" size="base" />
                  <Input
                    {...register("pageTitle")}
                    placeholder="Enter page title"
                    size="base"
                    validityState={errors.pageTitle ? "invalid" : "valid"}
                  />
                  {errors.pageTitle && (
                    <span className="text-danger text-sm font-medium">
                      {errors.pageTitle.message}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Controller
                    name="pageDescription"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        label="Page Description"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Enter page description"
                        size="base"
                        textLength="short"
                      />
                    )}
                  />
                  {errors.pageDescription && (
                    <span className="text-danger text-sm font-medium">
                      {errors.pageDescription.message}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Label label="Social Preview Image" size="base" />
                    {watch("openGraphImage") && (
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleReplaceImage}
                        >
                          Replace
                        </Button>
                      </div>
                    )}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />

                  {watch("openGraphImage") ? (
                    <div className="mt-1 flex items-center w-full h-24 border-1 border-border rounded-lg p-4 gap-3">
                      <img
                        src={imagePreview ?? watch("openGraphImage") ?? ""}
                        alt="Uploaded preview"
                        className="w-16 h-16 object-cover rounded flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="text-base text-default font-medium">
                          Open Graph Image
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="mt-1 flex items-center w-full h-24 border-1 border-border-component border-dashed rounded-lg bg-sheet p-4 gap-3 cursor-pointer hover:bg-surface transition-colors"
                      onClick={handleUploadClick}
                    >
                      <img
                        src="/images/chat/ImageBox.png"
                        alt="Upload placeholder"
                        className="w-12 h-12 flex-shrink-0"
                      />
                      <div className="flex-1">
                        <div className="text-base text-muted font-medium">
                          Upload an image
                        </div>
                        <div className="text-sm text-muted font-normal">
                          1200×630 pixels • 20MB max
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Label label="Search Results Preview" size="base" />
                  <div className="mt-1 p-4 border border-border-component rounded-lg bg-white">
                    {watch("openGraphImage") && imagePreview && (
                      <div className="mb-3">
                        <div className="w-full aspect-[1200/630] rounded overflow-hidden">
                          <img
                            src={imagePreview}
                            alt="Social preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    )}
                    <div className="text-primary text-base font-medium">
                      {watch("pageTitle")}
                    </div>
                    <div className="text-sm mt-1">
                      {watch("pageDescription")}
                    </div>
                    <div className="flex items-center gap-1 text-muted text-sm mt-1">
                      {project?.shopMetadataLightModeFaviconUrl ||
                      project?.shopMetadataDarkModeFaviconUrl ? (
                        <img
                          src={
                            project.shopMetadataLightModeFaviconUrl ??
                            project.shopMetadataDarkModeFaviconUrl ??
                            undefined
                          }
                          alt="Site favicon"
                          className="w-4 h-4 flex-shrink-0"
                        />
                      ) : (
                        <span>🌐</span>
                      )}
                      {defaultCustomDomain?.value ??
                        (project?.shopDomainPrefix
                          ? `${project.shopDomainPrefix}.reploshops.com`
                          : "your-store.reploshops.com")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 pb-6 px-6">
          <Button
            variant="secondary"
            size="base"
            onClick={handleDiscard}
            disabled={!isDirty}
          >
            Discard
          </Button>
          <Button
            variant="primary"
            size="base"
            onClick={() => void handleSubmit(handleSave)()}
            disabled={
              !isDirty ||
              Object.keys(errors).length > 0 ||
              isSaving ||
              isImageUploadLoading
            }
            isLoading={isSaving || isImageUploadLoading}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
